import React, { FC, useContext, useEffect, useState } from 'react';
import { PlayerID, TeamID } from '../../../scripts/models/common';
import { GameModel, TeamModel } from '../../../scripts/models/models';
import { DataContext } from '../../context/dataLoader/DataLoader';

import './SeasonRecord.scss';

type PartialGameModel = GameModel | {
    complete: boolean
    homeTeam: {
        id: TeamID,
        name: string
    },
    awayTeam: {
        id: TeamID,
        name: string
    },
    gameStates: {
        homeScore: number,
        awayScore: number
    }[],
    awayPitcher: {
        id: PlayerID
    },
    homePitcher: {
        id: PlayerID
    }
}

function scoreForGame(game: PartialGameModel, teamId: TeamID) {
    if (!game.complete) {
        return 0;
    }
    const wasHomeGame = game.homeTeam.id === teamId;
    const endState = game.gameStates[0];
    if (wasHomeGame) {
        return endState.homeScore - endState.awayScore;
    }

    return endState.awayScore - endState.homeScore;
}

function gameHasPitcher(game: PartialGameModel, pitcher: PlayerID) {
    return (game.awayPitcher.id === pitcher) || (game.homePitcher.id === pitcher)
}

function opposingTeamName(game: PartialGameModel, teamId: TeamID) {
    const wasHomeGame = game.homeTeam.id === teamId;
    return (wasHomeGame) ? game.awayTeam.name : game.homeTeam.name;
}


interface IProps {
    team: TeamModel,
    pitcherHighlight?: PlayerID,
}

const SeasonRecord: FC<IProps> = (props) => {
    const { team, pitcherHighlight } = props

    const { allGames, simModel } = useContext(DataContext);

    const normalSeasonGames = allGames?.currentSeasonGames

    // Smoothly handle team switch
    const [displayTeam, setDisplayTeam] = useState<TeamID>()
    const [atZero, setAtZero] = useState(true);
    useEffect(() => {
        setAtZero(true);
        setTimeout(() => {
            setDisplayTeam(team.id);
            setTimeout(() => {
                setAtZero(false)
            }, 100);
        }, 200);
    }, [team.id]);


    const currentDay = simModel?.simData.currentDay || 0;
    const gamesRaw = (displayTeam && normalSeasonGames?.gamesByTeam[displayTeam]) || []

    const games: PartialGameModel[] = [];
    for (let i = 0; i <= currentDay; i++) {
        const dayMatch = gamesRaw.filter(game => game.day === i);
        if (dayMatch.length === 1 && dayMatch[0].complete) {
            games.push(dayMatch[0])
        } else {
            games.push({
                complete: true,
                homeTeam: {
                    id: team.id,
                    name: ""
                },
                awayTeam: {
                    id: team.id,
                    name: ""
                },
                homePitcher: {
                    id: "" as PlayerID,
                },
                awayPitcher: {
                    id: "" as PlayerID,
                },
                gameStates: [
                    {
                        homeScore: 0,
                        awayScore: 0
                    }
                ]
            })
        }
    }


    const scale = Math.max(1, Math.min(8, Math.floor(90 / (currentDay + 1))));
    const barWidth = 4 * scale;
    const barMargin = scale;

    return (
        <div className="SeasonRecord">
            <div className="GamesChart">
                <div className="GamesChart-Centerline" />
                {
                    games.map((game, idx) => {
                        if (!game.complete || !displayTeam) {
                            return null;
                        }
                        let scoreDiff = scoreForGame(game, displayTeam);


                        let className = `GameBar ${scoreDiff < 0 ? "negative" : ""}`

                        if (pitcherHighlight !== undefined) {
                            const emphasis = gameHasPitcher(game, pitcherHighlight)
                            className += " " + (emphasis ? "emphasize" : "deemphasize");
                        }


                        const transition = `max-height ease 0.2s ${atZero ? "0" : idx / 30}s`;
                        const scoreText = (scoreDiff > 0 ? "+" : "") + scoreDiff;
                        const title = "Day " + (idx + 1) + ": " + scoreText + " against " + opposingTeamName(game, displayTeam);
                        const style = {
                            height: Math.abs(scoreDiff * 5) + "px", width: barWidth, marginLeft: barMargin,
                            maxHeight: atZero ? "0px" : "100px",
                            transition: transition
                        };

                        return <div key={idx} className={className} style={style} title={title} />
                    })
                }
            </div>
        </div>
    );
}

export default SeasonRecord;